import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { panelsHeaderMessages } from "@/i18n/messages/panels-header";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { InlineEditText } from "@/components/ui/inline-edit-text";
import { SpaceSwitcher } from "@/components/panels/space-switcher";
import type { SwitcherProject } from "@/components/panels/space-switcher";
import { getSpaceSwitcherMaxWidth } from "@/components/panels/space-switcher-layout";
import { isTauri } from "@/lib/is-tauri";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { AppUtilityControls } from "@/components/panels/app-utility-controls";
import { FocusChromeRevealHandle } from "@/components/layout/focus-chrome-autohide";
import { useFocusChromeAutoHide } from "@/components/layout/focus-chrome-autohide-store";
import {
  surfaceHoverClassName,
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";

/**
 * App header — floating glass islands.
 *
 * Two floating pills (left + right) with transparent middle.
 * Center island renders the project switcher when projects are provided.
 * Theme toggle uses local state + localStorage.
 */

interface AppHeaderProps {
  /** Current project name */
  projectName?: string;
  /** Current space name */
  spaceName?: string;
  /** Callback to rename the current project */
  onRenameProject?: (name: string) => void;
  /** Callback to rename the current space */
  onRenameSpace?: (name: string) => void;
  /** Projects with spaces for the center SpaceSwitcher */
  switcherProjects?: SwitcherProject[];
  /** Callback when a project icon is clicked */
  onSelectProject?: (projectId: string) => void;
  /** Callback when a space dot is clicked */
  onSelectSpace?: (spaceId: string) => void;
  /** Callback when add-project is clicked */
  onAddProject?: () => void;
  /** Callback when add-space is clicked */
  onAddSpace?: () => void;
  /** Optional right-click menu renderer for project avatars in the switcher */
  renderProjectContextMenu?: (project: SwitcherProject) => ReactNode;
  /** Optional right-click menu renderer for space dots in the switcher */
  renderSpaceContextMenu?: (
    project: SwitcherProject,
    space: SwitcherProject["spaces"][number],
  ) => ReactNode;
  /** User fallback letter for profile surfaces */
  userLetter?: string;
  /** Callback when search button is clicked */
  onSearch?: () => void;
  onShortcuts?: () => void;
  /** Callback when the notification expand button is clicked (opens search modal on notifications tab) */
  onOpenNotifications?: () => void;
  /** Callback when Settings is selected from the user menu */
  onSettings?: () => void;
  /** Callback when Logout is selected from the user menu */
  onLogout?: () => void;
  /** Whether the current space is the onboarding space */
  isOnboarding?: boolean;
  /** Callback to replay the onboarding tour */
  onReplayTour?: () => void;
  /** Show the left project chrome. Used when side chrome is paired to an open panel. */
  showLeftIsland?: boolean;
  /** Show the right utility chrome. Used when side chrome is paired to an open panel. */
  showRightIsland?: boolean;
  /** Show the center workspace chrome. Hidden by desktop Denk/workspace side-panel mode. */
  showCenterChrome?: boolean;
  className?: string;
  "data-testid"?: string;
}

export function AppHeader({
  projectName,
  onRenameProject,
  switcherProjects,
  onSelectProject,
  onSelectSpace,
  onAddProject,
  renderProjectContextMenu,
  userLetter = "D",
  onSearch,
  onShortcuts,
  onOpenNotifications,
  onSettings,
  onLogout,
  isOnboarding,
  onReplayTour,
  showLeftIsland = true,
  showRightIsland = true,
  showCenterChrome = true,
  className,
  "data-testid": testId = "app-header",
}: AppHeaderProps) {
  const t = useT(panelsHeaderMessages);
  // MasterLayout owns whether the center workspace chrome is visible. Keeping
  // that decision at the shell layer avoids a second desktop switcher rule here.
  const desktopMode = useDesktopMode((s) => s.mode);
  const hideDesktopOverlayIslands = isTauri() && desktopMode === "peek";
  const renderLeftIsland = showLeftIsland && !hideDesktopOverlayIslands;
  const renderRightIsland = showRightIsland && !hideDesktopOverlayIslands;
  const { focusActive, focusChromeHidden, focusChromeRegionProps } =
    useFocusChromeAutoHide();
  const renderCenterChrome = showCenterChrome && !focusChromeHidden;
  const rightOnlyHeader =
    !renderLeftIsland && !renderCenterChrome && renderRightIsland;
  const switcherSlotWidth = switcherProjects?.length
    ? getSpaceSwitcherMaxWidth(switcherProjects, "regular", {
        projectOnly: true,
      })
    : undefined;

  return (
    <header
      className={cn(
        "pointer-events-none relative flex h-11 w-full shrink-0 items-center justify-between py-0",
        rightOnlyHeader ? "px-0" : "px-3",
        className,
      )}
      data-testid={testId}
    >
      {renderLeftIsland && (
        <div
          className={cn(
            surfaceRoleClassName("chrome"),
            "pointer-events-auto flex h-11 w-60 items-center gap-1.5 rounded-full pl-1.5 pr-3 transition-[width] duration-200 focus-within:w-auto focus-within:min-w-60 focus-within:max-w-[24rem]",
          )}
          data-testid="header-left"
          {...surfaceRoleAttributes("chrome", {
            nativeLevel: "root",
            nativeGroup: "header-left",
          })}
        >
          {projectName && (
            <div
              className="flex min-w-0 flex-1 items-center gap-1"
              data-testid="header-project-space"
            >
              {projectName &&
                (onRenameProject ? (
                  <InlineEditText
                    value={projectName}
                    onSave={onRenameProject}
                    maxLength={50}
                    className="truncate text-appkit-caption font-semibold"
                    data-testid="header-project-name"
                  />
                ) : (
                  <span className="truncate text-appkit-caption font-semibold text-primary">
                    {projectName}
                  </span>
                ))}
            </div>
          )}

          {isOnboarding && onReplayTour && (
            <button
              type="button"
              className={cn(
                surfaceHoverClassName(),
                "group relative ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-transparent bg-transparent text-appkit-subhead leading-none transition-colors hover:text-primary",
              )}
              onClick={onReplayTour}
              data-testid="replay-tour-btn"
              aria-label={t("replayTour")}
            >
              <span aria-hidden="true">🎉</span>
              <span className="pointer-events-none absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-glass-fill-dense px-1.5 py-0.5 text-appkit-mini text-secondary opacity-0 shadow-glass-sm transition-opacity group-hover:opacity-100">
                {t("replayTour")}
              </span>
            </button>
          )}
        </div>
      )}

      {/* Center chrome: project switcher (or create-project button when empty) */}
      {showCenterChrome && (
        <div
          className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2"
          data-testid="focus-chrome-top-region"
          {...focusChromeRegionProps}
        >
          {focusChromeHidden ? (
            <FocusChromeRevealHandle side="top" />
          ) : (
            <div
              className={cn(
                "denker-logo-status-cluster pointer-events-none flex h-11 items-center gap-2",
                focusActive && "denker-focus-chrome-surface-in-top",
              )}
              data-testid="header-center"
            >
              {switcherProjects && switcherProjects.length > 0 ? (
                <>
                  <div
                    className="pointer-events-none flex justify-start"
                    style={{ width: switcherSlotWidth }}
                    data-testid="space-switcher-anchor"
                  >
                    <SpaceSwitcher
                      projects={switcherProjects}
                      projectOnly
                      onSelectProject={onSelectProject ?? (() => {})}
                      onSelectSpace={onSelectSpace ?? (() => {})}
                      onAddProject={onAddProject}
                      renderProjectContextMenu={renderProjectContextMenu}
                    />
                  </div>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    surfaceRoleClassName("chrome"),
                    "pointer-events-auto h-11 gap-2 rounded-full px-4",
                  )}
                  onClick={onAddProject}
                  data-testid="header-create-project"
                  {...surfaceRoleAttributes("chrome", {
                    nativeLevel: "root",
                    nativeGroup: "space-switcher",
                  })}
                >
                  <Icons.Plus className="h-4 w-4" />
                  <span className="text-appkit-caption font-medium">
                    {t("newProject")}
                  </span>
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Right island: search, theme, and avatar. */}
      {renderRightIsland && (
        <div
          className={cn(
            surfaceRoleClassName("chrome"),
            "pointer-events-auto absolute top-1/2 z-10 flex h-11 w-60 -translate-y-1/2 items-center gap-1.5 rounded-full p-1.5",
            "right-0",
          )}
          data-testid="header-right"
          {...surfaceRoleAttributes("chrome", {
            nativeLevel: "root",
            nativeGroup: "header-right",
          })}
        >
          <AppUtilityControls
            onSearch={onSearch}
            onShortcuts={onShortcuts}
            userLetter={userLetter}
            onOpenNotifications={onOpenNotifications}
            onSettings={onSettings}
            onLogout={onLogout}
            showUserMenu={!isTauri()}
          />
        </div>
      )}
    </header>
  );
}

export type { AppHeaderProps };
